import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  UseQueryOptions,
  QueryKey,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { API } from "../../utils/API/API";

interface CourseListParams {
  page?: string;
  limit?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  type?: string;
  tag?: string;
  search?: string;
  status?: CourseStatus | "all";
  groupBy?: string | "tag";
}

export interface GroupedCourse {
  _id: string;
  courses: ICourse[];
}
export interface IGroupedCourses {
  coursesByTag: GroupedCourse[];
  currentPage: number;
  itemsPerPage: number;
  total: number;
  totalPages: number;
}

export type ModuleOption = {
  populate: {
    course?: boolean;
    lesson?: boolean;
    progress?: boolean;
  };
};

export type RemoveMediaBody = { urls: string[]; course: string };

export type GenerateLessonBody = {
  lessonName: string;
  requirements: string[];
  customLesson: string;
};

export type AlternateBody = {
  lessonName: string;
  tool: string;
  requirement: string;
};

export enum CourseStatus {
  DRAFT = "DRAFT",
  PUBLISHED = "PUBLISHED",
  DEACTIVATE = "DEACTIVATE",
  PENDING = "PENDING",
}

export enum ModuleStatus {
  PUBLISHED = "PUBLISHED",
  ARCHIVED = "ARCHIVED",
}

export enum Difficulty {
  BEGINNER = "BEGINNER",
  INTERMEDIATE = "INTERMEDIATE",
  ADVANCED = "ADVANCED",
}

export enum ModuleType {
  content = "CONTENT",
  practice = "PRACTICE",
}

export const useCourses = (
  params: CourseListParams = {},
  options?: Omit<
    UseQueryOptions<
      IApiResponse<{ courses: ICourse[] }>,
      Error,
      IApiResponse<{ courses: ICourse[] }>,
      QueryKey
    >,
    "queryKey" | "queryFn"
  >
) => {
  return useQuery<
    IApiResponse<{ courses: ICourse[] }>,
    Error,
    IApiResponse<{ courses: ICourse[] }>,
    QueryKey
  >({
    queryKey: ["courseList", params],
    queryFn: () => API.fetchCourses(params) as any,
    ...options,
  });
};

export const usePublicCourses = (
  isLoggedIn?: boolean
): UseQueryResult<GroupedCourse[], AxiosError> => {
  return useQuery({
    queryKey: ["public, courses"],
    queryFn: () => API.publicCourses(),
    enabled: !isLoggedIn,
  });
};

export const usePublicCourse = (
  // isLoggedIn: boolean,
  id: string
): UseQueryResult<ICourse, AxiosError> => {
  return useQuery({
    queryKey: ["public, course", id],
    queryFn: () => API.publicCourse(id),
  });
};

export const useGetCourse = <T extends { populate?: boolean } = {}>(
  id: string
): UseQueryResult<IApiResponse<ICourse<T>>, AxiosError> => {
  return useQuery({
    queryKey: ["course", id],
    queryFn: () => API.course(id),
  });
};

export const useDeleteCourse = () => {
  return useMutation({
    mutationFn: (id: string) => API.deleteCourse(id),
  });
};

export const useCreateCourse = (): UseMutationResult<
  ICourse,
  AxiosError<{ message: string }>,
  Partial<ICourse> & any,
  unknown
> => {
  return useMutation({
    mutationFn: (courseData: Partial<ICourse>) => API.createCourse(courseData),
  });
};
export const useUpdateCourse = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData | any }) =>
      API.updateCourse(id, data),
  });
};

export const usePublishCourse = () => {
  return useMutation({
    mutationFn: (id: string) => API.publishCourse(id),
  });
};

export const useCourseClearChanges = () => {
  return useMutation({
    mutationFn: (id: string) => API.clearCourseChanges(id),
  });
};

export const useGetModule = (
  id: string
): UseQueryResult<IModule<any>, AxiosError> => {
  return useQuery({
    queryKey: ["module", id],
    queryFn: () => API.getModule(id),
  });
};

export const useModuleCreate = () => {
  return useMutation({
    mutationFn: (moduleData: Partial<IModule<any>>) =>
      API.createModule(moduleData),
  });
};

export const useModuleUpdate = () => {
  return useMutation({
    mutationFn: (moduleData: Partial<IModule<any>>) =>
      API.updateModule(moduleData),
  });
};

export const useModuleDelete = () => {
  return useMutation({
    mutationFn: (id: string) => API.deleteModule(id),
  });
};

export const useContentCreate = () => {
  return useMutation({
    mutationFn: (content: Partial<ILesson>) => API.createContent(content),
  });
};

export const useContentDelete = () => {
  return useMutation({
    mutationFn: (id: string) => API.deleteContent(id),
  });
};

export const useUploadMedia = () => {
  return useMutation<IApiResponse<string[]>, Error, FormData>({
    mutationFn: (body: FormData) => API.uploadMedia(body),
  });
};

export const useRemoveMedia = () => {
  return useMutation<IApiResponse<string>, Error, RemoveMediaBody>({
    mutationFn: (body: RemoveMediaBody) => API.removeMedia(body),
  });
};

export const useGenerateLesson = (): UseMutationResult<
  IApiResponse<{ lesson: IConfig[] }>,
  AxiosError<{ message: string }>,
  GenerateLessonBody,
  unknown
> => {
  return useMutation({
    mutationFn: (body: GenerateLessonBody) => API.generateLesson(body),
  });
};

export const useGetAlterante = (): UseMutationResult<
  any,
  AxiosError<{ message: string }>,
  AlternateBody,
  unknown
> => {
  return useMutation({
    mutationFn: (body: AlternateBody) => API.alternateContent(body),
  });
};

export const useSaveLesson = () => {
  return useMutation({
    mutationFn: (body: ILesson) => API.saveLesson(body),
  });
};

export const useSavePractice = () => {
  return useMutation({
    mutationFn: (body: IPractice) => API.savePractice(body),
  });
};

export const usePublishLesson = () => {
  return useMutation({
    mutationFn: (body: { _id: string }) => API.publishLesson(body),
  });
};

export const usePublishPractice = () => {
  return useMutation({
    mutationFn: (body: { _id: string }) => API.publishPractice(body),
  });
};

export const useGetTools = (): UseQueryResult<string[], AxiosError> => {
  return useQuery({
    queryKey: ["tools"],
    queryFn: () => API.getTools(),
  });
};

export const useDeleteLesson = () => {
  return useMutation({
    mutationFn: (id: string) => API.deleteLesson(id),
  });
};

export const useDeletePractice = () => {
  return useMutation({
    mutationFn: (id: string) => API.deletePractice(id),
  });
};
